/**
 * The look of a public booking page: the page's saved theme, overridden per embed by the host site
 * (MeetPoint-compatible params: accent, ink, font, hfont, hidebiz; plus bg, radius, mode).
 * Every value is validated here and applied only as CSS custom properties, so a query string can't
 * inject CSS; fonts load only from the allowlist below.
 */
export const FONTS: Record<string, { label: string; query: string | null; stack: string }> = {
  system: { label: 'System (SF / Segoe)', query: null, stack: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif' },
  inter: { label: 'Inter', query: 'Inter:wght@400..800', stack: '"Inter", ui-sans-serif, system-ui, sans-serif' },
  'public-sans': { label: 'Public Sans', query: 'Public+Sans:wght@400..700', stack: '"Public Sans", ui-sans-serif, system-ui, sans-serif' },
  'dm-sans': { label: 'DM Sans', query: 'DM+Sans:wght@400..700', stack: '"DM Sans", ui-sans-serif, system-ui, sans-serif' },
  montserrat: { label: 'Montserrat', query: 'Montserrat:wght@400..700', stack: '"Montserrat", ui-sans-serif, system-ui, sans-serif' },
  newsreader: { label: 'Newsreader (serif)', query: 'Newsreader:opsz,wght@6..72,400..600', stack: '"Newsreader", Georgia, serif' },
  'source-serif': { label: 'Source Serif (serif)', query: 'Source+Serif+4:opsz,wght@8..60,400..700', stack: '"Source Serif 4", Georgia, serif' },
  lora: { label: 'Lora (serif)', query: 'Lora:wght@400..700', stack: '"Lora", Georgia, serif' },
  playfair: { label: 'Playfair Display (serif)', query: 'Playfair+Display:wght@400..700', stack: '"Playfair Display", Georgia, serif' },
};

export type BookingTheme = { mode?: 'light' | 'dark' | undefined; accent?: string | undefined; ink?: string | undefined; bg?: string | undefined; font?: string | undefined; headingFont?: string | undefined; radius?: number | undefined; hideBusiness?: boolean | undefined };

const hex = (v: unknown) => (typeof v === 'string' && /^#?[0-9a-f]{6}$/i.test(v.trim()) ? `#${v.trim().replace('#', '').toLowerCase()}` : undefined);
const font = (v: unknown) => (typeof v === 'string' && v in FONTS ? v : undefined);

export function clean(t: Record<string, unknown>): BookingTheme {
  const r = Number(t.radius);
  const out: BookingTheme = {
    ...(t.mode === 'light' || t.mode === 'dark' ? { mode: t.mode } : {}),
    ...(hex(t.accent) ? { accent: hex(t.accent)! } : {}),
    ...(hex(t.ink) ? { ink: hex(t.ink)! } : {}),
    ...(hex(t.bg) ? { bg: hex(t.bg)! } : {}),
    ...(font(t.font) ? { font: font(t.font)! } : {}),
    ...(font(t.headingFont) ? { headingFont: font(t.headingFont)! } : {}),
    ...(Number.isInteger(r) && r >= 0 && r <= 24 && t.radius !== undefined && t.radius !== '' ? { radius: r } : {}),
    ...(t.hideBusiness === true ? { hideBusiness: true } : {}),
  };
  return out;
}

/** Page theme + the embed's query string (embed wins). */
export function resolveTheme(saved: Record<string, unknown> | undefined, search: string) {
  const q = new URLSearchParams(search);
  const fromQuery = clean({ mode: q.get('mode') ?? undefined, accent: q.get('accent') ?? undefined, ink: q.get('ink') ?? undefined, bg: q.get('bg') ?? undefined,
    font: q.get('font') ?? undefined, headingFont: q.get('hfont') ?? undefined, radius: q.get('radius') ?? undefined, hideBusiness: q.get('hidebiz') === '1' ? true : undefined });
  return { theme: { ...clean(saved ?? {}), ...fromQuery }, embed: q.get('embed') === '1' };
}

const lum = (h: string) => { const [r, g, b] = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)); return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!; };

/** The kit's variables, re-pointed at this page's look. Spread onto the page wrapper's style. */
export function themeVars(t: BookingTheme): Record<string, string> {
  const v: Record<string, string> = {};
  if (t.accent) {
    Object.assign(v, { '--cx-accent': t.accent, '--cx-accent-text': t.accent, '--cx-eyebrow': t.accent, '--cx-link': t.accent, '--cx-accent-alt': t.accent,
      '--cx-on-accent': lum(t.accent) > 0.45 ? '#0b1218' : '#ffffff' });
  }
  if (t.bg) Object.assign(v, { '--cx-panel': t.bg, '--cx-panel-deep': t.bg, '--cx-control': t.bg });
  if (t.ink) {
    const base = t.bg ?? (t.mode === 'dark' ? '#10263a' : '#ffffff');
    Object.assign(v, { '--cx-text': t.ink, '--cx-text-muted': `color-mix(in srgb, ${t.ink} 72%, ${base})`, '--cx-text-faint': `color-mix(in srgb, ${t.ink} 58%, ${base})`,
      '--cx-line': `color-mix(in srgb, ${t.ink} 12%, transparent)`, '--cx-line-strong': `color-mix(in srgb, ${t.ink} 26%, transparent)` });
  }
  if (t.font) v['--cx-font-sans'] = FONTS[t.font]!.stack;
  if (t.headingFont) v['--bk-heading'] = FONTS[t.headingFont]!.stack;
  if (t.radius !== undefined) {
    const r = t.radius;
    Object.assign(v, { '--cx-r-hero': `${r}px`, '--cx-r-panel': `${r}px`, '--cx-r-card': `${Math.round(r * 0.8)}px`, '--cx-r-row': `${Math.round(r * 0.6)}px`, '--cx-r-control': `${Math.round(r * 0.5)}px` });
  }
  return v;
}

/** Load only allowlisted Google Fonts, once. */
export function loadFonts(t: BookingTheme) {
  const q = [...new Set([t.font, t.headingFont])].map((f) => (f ? FONTS[f]?.query : null)).filter(Boolean);
  if (!q.length) return;
  const href = `https://fonts.googleapis.com/css2?${q.map((x) => `family=${x}`).join('&')}&display=swap`;
  if (document.querySelector(`link[href="${href}"]`)) return;
  const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = href; document.head.appendChild(l);
}
